import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import { ReceiptIcon } from '@phosphor-icons/react';
import { fetchEnhancedSalesOrderUiEnabled } from '@/api/org/config/useEnhancedSalesOrderUi';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../route';

// OMS v3 owns the unversioned path. Its page components intentionally stay in
// ../sales-orders-v3/ (folder not renamed to avoid churn); the legacy Sales
// Orders page components live beside this module and are served at
// /sales-orders-v2.
const SalesOrdersV3Page = lazyRouteComponent(
  () => import('../sales-orders-v3/SalesOrdersV3Page'),
);

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders',
  staticData: {
    app: {
      nav: {
        label: 'Sales Orders',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrdersV3Page,
  // Persist pagination in the URL so a refresh / shared link returns to the
  // same page and page size. `salesOrderId` is a legacy v2 deep-link param
  // declared only so it survives the legacy-org redirect below (zod strips
  // undeclared keys before beforeLoad runs).
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    salesOrderId: z.string().optional(),
  }),
  loader: () => ({
    crumb: 'Sales Orders',
  }),
  beforeLoad: async ({ context, search }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    // TEMPORARY redirect — remove after all tenants migrate to OMS v3.
    // Legacy orgs keep the old pages: redirect BEFORE the v3 permission check
    // so a user without SalesOrderDraftV3 lands on /sales-orders-v2, not
    // /unauthorized.
    if (!(await fetchEnhancedSalesOrderUiEnabled(context.queryClient))) {
      throw redirect({ to: '/sales-orders-v2', search });
    }
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});
